import type { BuilderConfig } from './builder-types';
import { resolveTechSpec, STACK_CATEGORIES } from './tech-spec/index';
import type { TechSpecState } from './tech-spec/types';

interface SpecLike {
  name: string;
  slug: string;
}

interface FeatureEntry {
  slug: string;
  name: string;
}

export function exportAsTechPrompt(
  spec: SpecLike,
  builderConfig: BuilderConfig | null,
  techState: TechSpecState,
  features: FeatureEntry[],
): string {
  const { stack, customizations } = techState;

  const enabledFeatures = features.filter((f) => {
    if (!builderConfig) return true;
    return builderConfig.features[f.slug]?.enabled ?? false;
  });

  const getLabel = (categoryId: string, value: string) => {
    const cat = STACK_CATEGORIES.find((c) => c.id === categoryId);
    return cat?.options.find((o) => o.id === value)?.label ?? value;
  };

  const lines: string[] = [
    `You are implementing the following technical specification. Build exactly what is described.`,
    '',
    `# ${spec.name} — Technical Spec`,
    '',
    `## Stack`,
    `- **Frontend:** ${getLabel('frontend', stack.frontend)}`,
    `- **Backend:** ${getLabel('backend', stack.backend)}`,
    `- **Database:** ${getLabel('database', stack.database)}`,
    `- **Auth:** ${getLabel('authProvider', stack.authProvider)}`,
    `- **Realtime:** ${getLabel('realtime', stack.realtime)}`,
    `- **Hosting:** ${getLabel('hosting', stack.hosting)}`,
    '',
    '---',
    '',
  ];

  for (const feature of enabledFeatures) {
    const featureConfig = builderConfig?.features[feature.slug];
    const spec2 = resolveTechSpec(
      feature.slug,
      stack,
      featureConfig,
      customizations[feature.slug],
    );

    const hasContent =
      spec2.pages.length > 0 ||
      spec2.uxFlows.length > 0 ||
      spec2.endpoints.length > 0 ||
      spec2.dbTables.length > 0;

    if (!hasContent) continue;

    lines.push(`## Feature: ${feature.name}`, '');

    if (spec2.pages.length > 0) {
      lines.push('### Pages / Routes', '');
      lines.push('| Path | Description | Auth |');
      lines.push('|------|-------------|------|');
      for (const page of spec2.pages) {
        lines.push(
          `| \`${page.path}\` | ${page.description} | ${page.authRequired ? 'Required' : 'Public'} |`,
        );
      }
      lines.push('');
    }

    if (spec2.uxFlows.length > 0) {
      lines.push('### UX Flows', '');
      for (const flow of spec2.uxFlows) {
        lines.push(`**${flow.title}**`);
        flow.steps.forEach((step, i) => {
          lines.push(`${i + 1}. ${step}`);
        });
        lines.push('');
      }
    }

    if (spec2.endpoints.length > 0) {
      lines.push('### API Endpoints', '');
      lines.push('| Method | Path | Description |');
      lines.push('|--------|------|-------------|');
      for (const ep of spec2.endpoints) {
        lines.push(`| \`${ep.method}\` | \`${ep.path}\` | ${ep.description} |`);
      }
      lines.push('');
    }

    if (spec2.dbTables.length > 0) {
      lines.push('### Database Tables', '');
      for (const table of spec2.dbTables) {
        lines.push(`**${table.name}**`);
        if (table.notes) lines.push(`> ${table.notes}`);
        if (table.columns.length > 0) {
          lines.push('');
          lines.push('| Column | Type | Nullable |');
          lines.push('|--------|------|----------|');
          for (const col of table.columns) {
            lines.push(
              `| \`${col.name}\` | ${col.type} | ${col.nullable ? 'yes' : 'no'} |`,
            );
          }
        }
        lines.push('');
      }
    }

    lines.push('---', '');
  }

  lines.push(
    `*Generated by [Specly](https://specly.danielarrizza.com) · Stack: ${getLabel('frontend', stack.frontend)} + ${getLabel('backend', stack.backend)} + ${getLabel('database', stack.database)}*`,
  );

  return lines.join('\n');
}

export function exportAsTechJson(
  spec: SpecLike,
  builderConfig: BuilderConfig | null,
  techState: TechSpecState,
  features: FeatureEntry[],
) {
  const { stack, customizations } = techState;

  const enabledFeatures = features.filter((f) => {
    if (!builderConfig) return true;
    return builderConfig.features[f.slug]?.enabled ?? false;
  });

  return {
    spec: { name: spec.name, slug: spec.slug },
    stack,
    features: Object.fromEntries(
      enabledFeatures.map((f) => [
        f.slug,
        resolveTechSpec(
          f.slug,
          stack,
          builderConfig?.features[f.slug],
          customizations[f.slug],
        ),
      ]),
    ),
  };
}
